






























import { Component, Mixins, Watch, Prop } from 'vue-property-decorator'
import { Playlist, PLAYLIST_TYPES } from '@/classes/playlist'
import { LibraryMixin } from '@/mixins'
import draggable from 'vuedraggable'
import * as _ from 'lodash'

export interface IPlaylistContainer {
  index: number
  parent: IPlaylistContainer | null
  playlist: Playlist
  selected: boolean
  collapsed: boolean
  hidden: boolean
}

interface IDragEndEvent {
  newIndex: number
  oldIndex: number
}

let playlistContainerIndex: number = 0

function generatePlaylistContainersRecursive(playlists: Playlist[], parent: IPlaylistContainer): IPlaylistContainer[] {
  let containers: IPlaylistContainer[] = []

  for (const pl of playlists) {
    const parentPlaylistContainer: IPlaylistContainer = {
      index: playlistContainerIndex++,
      parent,
      playlist: pl,
      selected: false,
      collapsed: false,
      hidden: false
    }

    containers.push(parentPlaylistContainer)

    containers = containers.concat(generatePlaylistContainersRecursive(pl.playlists, parentPlaylistContainer))
  }

  return containers
}

@Component({
  components: {
    Row: require('./Row').default,
    draggable,
    ProgressLoader: require('@/components/ProgressLoader').default
  }
})
export default class PlaylistView extends Mixins(LibraryMixin) {
  @Prop({
    required: true
  })
  value: Playlist[]

  @Prop({
    required: true
  })
  allowDrag: boolean

  @Prop()
  loading: boolean

  searchModel: string = ''
  selectedPlaylists: Playlist[] = this.value
  lastSelectedIndex: number = -1

  playlists: IPlaylistContainer[] = []

  dragOptions = {
    animation: 200,
    group: 'description',
    disabled: false,
    ghostClass: 'ghost'
  }

  get filteredPlaylists(): IPlaylistContainer[] {
    if (!this.searchModel) return this.playlists

    const s = this.searchModel.toLowerCase()

    const results = this.playlists.filter(x => x.playlist.name.toLowerCase().includes(s))

    return this.playlists.filter(x => results.some(r => r.playlist._id === x.playlist._id) || x.playlist.playlists.some(y => results.some(r => r.playlist._id === y._id)))
  }

  onDragEnd(event: IDragEndEvent) {
    if (event.oldIndex === event.newIndex) return

    const playlistContainer = this.playlists[event.newIndex]

    let playlistBeforeContainer: IPlaylistContainer | undefined
    let newParentPlaylist: Playlist | null = null

    if (event.newIndex > 0) {
      playlistBeforeContainer = this.playlists[event.newIndex - 1]
    }

    if (playlistBeforeContainer) {
      let isOwnChild: boolean = false

      let p: IPlaylistContainer | null = playlistBeforeContainer.parent

      while (p) {
        if (p?.playlist._id === playlistContainer.playlist._id) {
          isOwnChild = true

          break
        }

        p = p.parent
      }

      if (isOwnChild) {
        playlistBeforeContainer = this.playlists[event.oldIndex - 1]
      }

      let newParentPlaylistContainer: IPlaylistContainer | null = null

      if (playlistBeforeContainer && playlistBeforeContainer.playlist.type === PLAYLIST_TYPES.Folder) {
        newParentPlaylistContainer = playlistBeforeContainer
      } else if (playlistBeforeContainer) {
        newParentPlaylistContainer = playlistBeforeContainer.parent
      } else {
        let parent: IPlaylistContainer | null = playlistContainer.parent

        while (true) {
          if (parent && parent.parent) parent = parent.parent
          else break
        }

        newParentPlaylistContainer = parent
      }

      newParentPlaylist = newParentPlaylistContainer ? newParentPlaylistContainer.playlist : null
    } else {
      newParentPlaylist = playlistContainer.parent?.playlist || null
    }

    this.$emit('dragEnd', {
      playlist: playlistContainer.playlist,
      playlistBefore: playlistBeforeContainer ? playlistBeforeContainer.playlist : null,
      newParentPlaylist
    })
  }

  clearFilters() {
    this.searchModel = ''
  }

  clearSelection() {
    this.selectedPlaylists = []
  }

  onRowToggleSelected(con: IPlaylistContainer, shiftKey: boolean) {
    if (con.selected) {
      if (shiftKey && this.lastSelectedIndex > -1) {
        for (let i = Math.min(this.lastSelectedIndex, con.index) - 1; i < Math.max(this.lastSelectedIndex, con.index); i++) {
          if (!this.selectedPlaylists.some(x => x._id === this.playlists[i].playlist._id)) {
            this.selectedPlaylists.push(this.playlists[i].playlist)
          }
        }
      } else {
        this.selectedPlaylists.push(con.playlist)
      }

      this.lastSelectedIndex = con.index
    } else {
      const i = this.selectedPlaylists.findIndex(pl => pl._id === con.playlist._id)

      this.selectedPlaylists.splice(i, 1)

      this.lastSelectedIndex = -1
    }

    this.selectedPlaylists = _.sortBy(this.selectedPlaylists, x => _.findIndex(this.playlists, y => x._id === y.playlist._id))
  }

  onRowCollapse(con: IPlaylistContainer, hidden: boolean) {
    for (const pl of this.playlists) {
      if (pl.parent && pl.parent.playlist._id === con.playlist._id) {
        pl.hidden = hidden

        this.onRowCollapse(pl, hidden)
      }
    }
  }

  @Watch('library', { immediate: true })
  watchLibrary() {
    if (this.library) {
      playlistContainerIndex = 0

      const rootPlaylistContainer: IPlaylistContainer = {
        index: playlistContainerIndex++,
        parent: null,
        playlist: this.library.playlists[0],
        selected: false,
        collapsed: false,
        hidden: false
      }

      if (this.library.playlists.length > 0) {
        this.playlists = generatePlaylistContainersRecursive(this.library.playlists[0].playlists, rootPlaylistContainer)
      }
    }
  }

  @Watch('selectedPlaylists')
  watchSelectedPlaylists() {
    this.$emit('input', this.selectedPlaylists)
  }

  @Watch('value', { immediate: true })
  watchValue() {
    this.selectedPlaylists = this.value

    for (const pl of this.playlists) {
      pl.selected = false
    }

    this.selectedPlaylists.forEach(pl => {
      const playlist = this.playlists.find(x => x.playlist._id === pl._id)

      if (playlist) {
        playlist.selected = true
      }
    })
  }
}
